/**
 * XOP Networks' quotes TO a reseller (spec 2026-09-09 §5).
 *
 * NOT `ResellerInvoice`, and the distinction is the whole design. A
 * `ResellerInvoice` is a document the RESELLER issues to ITS customer, stored
 * in crm-api's `invoices` table and billed under the reseller's own name. An
 * `XopQuote` is a `reseller_quotes` row: XOP Networks priced it from the
 * price list, XOP Networks' identity is on it, and the reseller's only moves
 * are to accept it or decline it. They share nothing but the word "quote",
 * which is exactly why the portal used to be confusing.
 *
 * camelCase, like every other projection in this package: these keys are the
 * BFF's answer to the SPA, not crm-api's row. The snake_case contract lives
 * in `apps/server/src/routes/xopQuotes.ts` and stops there.
 */

import type { DocumentRef } from "./purchaseOrders.js";

/** What the RECORD holds. */
export const XOP_QUOTE_STATUSES = ["open", "accepted", "declined"] as const;

export type XopQuoteStatus = (typeof XOP_QUOTE_STATUSES)[number];

/**
 * What the reseller is SHOWN. "expired" is not a stored state — it is
 * `open` plus a `valid_until` in the past, and crm-api computes it as
 * `effective_status`. Kept as a separate enum so no component can offer an
 * Accept button by testing `status === "open"` on a quote that lapsed on
 * Sunday.
 */
export const XOP_QUOTE_EFFECTIVE_STATUSES = ["open", "accepted", "declined", "expired"] as const;

export type XopQuoteEffectiveStatus = (typeof XOP_QUOTE_EFFECTIVE_STATUSES)[number];

export const XOP_QUOTE_STATUS_LABELS: Record<XopQuoteEffectiveStatus, string> = {
  open: "Open",
  accepted: "Accepted",
  declined: "Declined",
  expired: "Expired",
};

export function isXopQuoteStatus(v: unknown): v is XopQuoteStatus {
  return typeof v === "string" && (XOP_QUOTE_STATUSES as readonly string[]).includes(v);
}

export function isXopQuoteEffectiveStatus(v: unknown): v is XopQuoteEffectiveStatus {
  return typeof v === "string" && (XOP_QUOTE_EFFECTIVE_STATUSES as readonly string[]).includes(v);
}

/** One priced line, snapshotted from the price-list row it was built from. */
export interface XopQuoteLine {
  /** The book row this line came from. Null only for a row crm-api sent
   * without one — the line is still quotable, it just cannot be traced. */
  priceListItemId: number | null;
  partNumber: string;
  description: string;
  /** The price-list unit (`port`, `year`, `each`…). Free text here rather
   * than `PriceListUnit`: this is a SNAPSHOT of what the book said when the
   * quote was issued, and a unit XOP Networks retires later must not stop the
   * old quote from rendering. */
  unit: string | null;
  quantity: number;
  /** Minor units. Integers throughout — a 30-line quote adds up in cents so
   * no float drift can make the printed total disagree with crm-api's. */
  unitPriceCents: number;
  lineTotalCents: number;
}

/** The bill-to block: the reseller, as XOP Networks has it on file. */
export interface XopQuoteBillTo {
  id: number | null;
  name: string | null;
  code: string | null;
  legalName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

/** The from block: XOP Networks, as crm-api has it from the tenant's company
 * fields (with crm-api's own fallback when those are blank). The quote PDF is
 * rendered by crm-api, so the portal fills no gaps here. */
export interface XopQuoteIssuer {
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
}

export interface XopQuote {
  id: number;
  /** `XQ-2026-0007`. Null only if crm-api sent a row without one, which
   * would be a bug there — the number is allocated in the same transaction. */
  number: string | null;
  status: XopQuoteStatus;
  effectiveStatus: XopQuoteEffectiveStatus;
  currency: string | null;
  lines: XopQuoteLine[];
  subtotalCents: number | null;
  totalCents: number | null;
  /** The note the reseller sent with the request. */
  note: string | null;
  issuedAt: string | null;
  validUntil: string | null;
  requestedByEmail: string | null;
  /** WHICH of the reseller's customers the quote is for — the name as it stood
   * when the quote was issued. A document's own record: renaming the customer
   * later must not rewrite what XOP quoted. */
  customerName: string | null;
  decidedAt: string | null;
  decidedByEmail: string | null;
  /** Why it was declined — or an accepting note. */
  decisionNote: string | null;
  reseller: XopQuoteBillTo;
  issuer: XopQuoteIssuer;
  /** The purchase order made from this quote once it was accepted — at most
   * one per quote (crm-api is idempotent about it). Null until one exists;
   * the quote page then offers "Create purchase order". */
  purchaseOrder: DocumentRef | null;
}

/** `GET /api/crm/xop-quotes`. */
export interface XopQuoteListResponse {
  quotes: XopQuote[];
}

/** `GET /api/crm/xop-quotes/:id`, and every write's answer. */
export interface XopQuoteResponse {
  quote: XopQuote;
}

export interface XopQuoteRequestLine {
  priceListItemId: number;
  quantity: number;
}

export interface XopQuoteRequestInput {
  /** WHICH of the reseller's own customers this quote is for. Required: a
   * quote with no recipient is not a quote XOP can act on, and crm-api
   * resolves the id inside the reseller's own subtree. */
  customerId: number;
  items: XopQuoteRequestLine[];
  note?: string;
}

export interface XopQuoteDecisionInput {
  note?: string;
}

/**
 * May this quote still be accepted or declined?
 *
 * The ONE predicate every decision control asks, so "open" can never be
 * decided in two places that disagree. Takes the narrowest shape it needs so
 * a test can call it with a literal.
 */
export function xopQuoteIsOpen(quote: { effectiveStatus: XopQuoteEffectiveStatus }): boolean {
  return quote.effectiveStatus === "open";
}

/**
 * Minor units → whole units, for `lib/format.ts`'s `money()`.
 *
 * Returns NULL for a missing amount rather than 0, because the portal's one
 * display rule is that a figure the CRM did not give us renders as an em
 * dash. `0` is a claim; `null` is the absence of one.
 */
export function centsToUnits(cents: number | null | undefined): number | null {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return null;
  return cents / 100;
}

/**
 * The running total of a request, in CENTS.
 *
 * Cents on purpose: the picker multiplies and sums thirty rows while someone
 * types, and doing that in whole units accumulates float error that makes the
 * number on screen disagree with the number crm-api computes. crm-api is the
 * authority on the issued total either way — this is what the reseller reads
 * while choosing.
 */
export function xopQuoteRequestTotalCents(
  rows: readonly { quantity: number; unitPriceCents: number }[],
): number {
  return rows.reduce(
    (sum, row) => sum + Math.round(row.quantity) * Math.round(row.unitPriceCents),
    0,
  );
}
